import React, { FC, useCallback } from 'react';
import { useTranslation } from '@shared/core';
import { ButtonV2, Flex } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { DialogFooterProps, PICKER_TYPES, TabType } from '../../PhotoPickerDialog.types';

export const DialogFooter: FC<DialogFooterProps> = props => {
  const {
    handleSelectAll,
    handleDeselect,
    handleCancel,
    handleAdd,
    isAdding,
    disableAddCTA,
    disableDeselectAllCTA = false,
    disableSelectAllCTA = false,
    pickerType = PICKER_TYPES.ADD_TO_COLLECTION,
    selectedItemsCount,
    supportsPhotoCrop,
    selectedTab,
    enableMobileCrop,
    primaryCTALabel,
  } = props;

  const { tq } = useTranslation('photoPickerDialog');

  const [isMobile] = useResponsive({ values: { xxs: true, sm: false } });

  const renderCTALabel = useCallback(() => {
    if (primaryCTALabel) {
      // If a custom cta label is provided, use it
      return primaryCTALabel;
    }
    if (pickerType === PICKER_TYPES.ADD_TO_COLLECTION) {
      return isMobile ? tq`Add` : tq`Add to Album`;
    }
    if (supportsPhotoCrop && selectedTab) {
      if (selectedTab === TabType.SELECT_PHOTO) {
        return tq`Select Photo`;
      }
      if (selectedTab === TabType.CROP || selectedTab === TabType.MOBILE_CROP) {
        if (enableMobileCrop) {
          return selectedTab === TabType.CROP ? tq`Desktop Crop` : tq`Mobile Crop`;
        }
        return tq`Crop`;
      }
    }
    if (Number(selectedItemsCount)) {
      return tq.plural(Number(selectedItemsCount), {
        zero: tq`Select Photo`,
        one: tq`Select 1 Photo`,
        other: tq`Select ${selectedItemsCount} Photos`,
      });
    } else {
      return tq`Select Photo`;
    }
  }, [enableMobileCrop, isMobile, pickerType, selectedItemsCount, selectedTab, supportsPhotoCrop, tq, primaryCTALabel]);

  return (
    <Flex
      flexDirection="row"
      justifyContent={pickerType === PICKER_TYPES.ADD_TO_COLLECTION ? 'space-between' : 'center'}
      width="100%"
    >
      <Flex
        flexDirection="row"
        gap={3}
      >
        {pickerType === PICKER_TYPES.ADD_TO_COLLECTION && (
          <>
            {handleSelectAll && (
              <ButtonV2
                size="lg"
                variant="ghost"
                shape="rounded"
                intent="neutral"
                onClick={handleSelectAll}
                disabled={disableSelectAllCTA}
              >
                {tq`Select All`}
              </ButtonV2>
            )}
            {handleDeselect && (
              <ButtonV2
                size="lg"
                variant="ghost"
                shape="rounded"
                intent="neutral"
                onClick={handleDeselect}
                disabled={disableDeselectAllCTA}
              >
                {tq`Deselect All`}
              </ButtonV2>
            )}
          </>
        )}
      </Flex>
      <Flex
        flexDirection="row"
        gap={3}
      >
        {pickerType === PICKER_TYPES.ADD_TO_COLLECTION && (
          <ButtonV2
            size="lg"
            display={isMobile ? 'none' : 'inline-flex'}
            variant="ghost"
            shape="rounded"
            intent="neutral"
            onClick={handleCancel}
            disabled={isAdding}
          >
            {tq`Cancel`}
          </ButtonV2>
        )}
        <ButtonV2
          size="lg"
          variant="solid"
          shape="rounded"
          intent="neutral"
          onClick={handleAdd}
          minWidth={isMobile ? pxToRem(80) : pxToRem(150)}
          disabled={disableAddCTA || isAdding}
          loading={isAdding}
        >
          {renderCTALabel()}
        </ButtonV2>
      </Flex>
    </Flex>
  );
};

DialogFooter.displayName = 'DialogFooter';
